import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../components/firebase";
import { signOut } from "firebase/auth";
import {
  Search, BarChart3, Wallet, PlusCircle, Layout, FileText,
  Edit3, ChevronDown, ChevronUp, Eye, LogOut, Printer,
  Calendar, User as UserIcon, FilePlus, Bell
} from "lucide-react";
import KpiFormModal from "../components/KpiFormModal";
import TabunganFormModal from "../components/TabunganFormModal";
import AddMasterModal from "../components/AddMasterModal"; 
import ScreenLoading from "../components/ScreenLoading";
import Modal from "../components/Modal";
import imageCompression from "browser-image-compression";
import logoAnnisa from "../assets/logo_web.png";
import "../styles/HomePage.css";

const API = process.env.REACT_APP_API_BASE_URL || "";

const extractFilenameFromUrl = (url) => {
  try { return url.split("/").pop().split("?")[0]; } 
  catch { return null; }
};

const shortenFilename = (name, lenFront = 6, lenBack = 6) => {
  if (!name) return "";
  if (name.length <= lenFront + lenBack + 3) return name;
  const front = name.slice(0, lenFront);
  const back = name.slice(name.length - lenBack);
  return `${front}...${back}`;
};

const cleanStr = (str) => String(str || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

const HomePage = ({ user, onLogout }) => {
  const [showTabunganForm, setShowTabunganForm] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showAddMasterModal, setShowAddMasterModal] = useState(false); 
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingKpi, setLoadingKpi] = useState(false);
  const [myKpiData, setMyKpiData] = useState({});
  const [expandedDates, setExpandedDates] = useState([]);
  const [editCounts, setEditCounts] = useState({});
  const [searchName, setSearchName] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [loadingTabungan, setLoadingTabungan] = useState(false);
  const [myTabunganData, setMyTabunganData] = useState({});
  const [totalTabungan, setTotalTabungan] = useState(0);
  const [savingData, setSavingData] = useState(false);
  const [totalKaryawan, setTotalKaryawan] = useState(0);
  const [totalKpi, setTotalKpi] = useState(0);
  const [showSuggest, setShowSuggest] = useState(false);
  const [editModalData, setEditModalData] = useState(null);
  const [modalData, setModalData] = useState({
    isOpen: false, type: "info", title: "", message: "", onConfirm: null,
  });

  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [loadingNotifs, setLoadingNotifs] = useState(true); 

  const navigate = useNavigate();

  const handleLogout = async () => {
    try { 
      localStorage.removeItem("printData");
      localStorage.removeItem("printData_RSIA");
      
      await signOut(auth); 
      if (onLogout) onLogout(); 
      navigate("/"); 
    } catch (error) { 
      setModalData({ isOpen: true, type: "error", title: "Logout Gagal", message: error.message }); 
    }
  };

  const currentData = activeTab === "kpi" ? myKpiData : myTabunganData;

  const allNames = useMemo(() => {
    return Array.from(new Set(Object.values(currentData || {}).flat().map((item) => item.nama).filter(Boolean)));
  }, [currentData]);

  const filteredSuggestions = useMemo(() => {
    if (!searchName) return [];
    return allNames.filter((name) => name.toLowerCase().includes(searchName.toLowerCase())).sort((a, b) => a.localeCompare(b));
  }, [searchName, allNames]);

  useEffect(() => {
    const timer = setTimeout(() => setLoadingPage(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!user || !user.name || !user.email) {
      setLoadingPage(false);
      return;
    }
    
    const fetchPendingApprovals = async (isFirstLoad = false) => {
      if (isFirstLoad) setLoadingNotifs(true);

      let tasksSet = new Set();
      
      try {
        const resApprover = await fetch(`${API}/api/pending-approvals`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ namaAtasan: user.name, emailAtasan: user.email })
        });
        const dataApprover = await resApprover.json();
        if (dataApprover.result === "success" && dataApprover.message) {
          const combined = [...(dataApprover.message.kpi || []), ...(dataApprover.message.tabungan || [])];
          combined.forEach(a => tasksSet.add(`${a.type}-${a.nama_karyawan}`));
        }
      } catch (err) {}

      let allNotifs = [];

      try {
        const pKpi = fetch(`${API}/api/kpi-by-user`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: user.email }) }).then(r => r.json()).catch(() => null);
        const pTab = fetch(`${API}/api/tabungan-by-user`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: user.email }) }).then(r => r.json()).catch(() => null);
        
        const [dataKpi, dataTab] = await Promise.all([pKpi, pTab]);

        const processItems = (dataObj, typeLabel) => {
          if (dataObj && dataObj.result === "success" && Array.isArray(dataObj.data)) {
            const grouped = {};
            dataObj.data.forEach(item => {
              if (item.status === "PENDING" || item.status === "APPROVED") {
                if (!grouped[item.nama]) grouped[item.nama] = { status: item.status, t1: item.ttd_atasan_1, t2: item.ttd_atasan_2 };
                if (item.status === "PENDING") grouped[item.nama].status = "PENDING";
                if (item.ttd_atasan_1) grouped[item.nama].t1 = item.ttd_atasan_1;
                if (item.ttd_atasan_2) grouped[item.nama].t2 = item.ttd_atasan_2;
              }
            });

            Object.keys(grouped).forEach(namaKaryawan => {
              const data = grouped[namaKaryawan];
              const isOwner = cleanStr(namaKaryawan) === cleanStr(user.name);
              const isTask = tasksSet.has(`${typeLabel}-${namaKaryawan}`);
              
              allNotifs.push({
                id: `${typeLabel}-${namaKaryawan}`,
                type: typeLabel,
                nama_karyawan: namaKaryawan,
                isOwner,
                isTask,
                status: data.status,
                ttd1: data.t1,
                ttd2: data.t2
              });
            });
          }
        };

        processItems(dataKpi, "kpi");
        processItems(dataTab, "tabungan");

      } catch (err) {}

      allNotifs.sort((a, b) => {
        if (a.isTask && !b.isTask) return -1;
        if (!a.isTask && b.isTask) return 1;
        if (a.isOwner && !b.isOwner) return -1;
        if (!a.isOwner && b.isOwner) return 1;
        return 0;
      });

      setPendingApprovals(allNotifs);
      setLoadingNotifs(false);
      setLoadingPage(false);
    };
    
    fetchPendingApprovals(true);
    
    const interval = setInterval(() => fetchPendingApprovals(false), 30000); 

    const handleFocus = () => {
      fetchPendingApprovals(false);
    };
    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
    };
  }, [user]);

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader(); reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result); reader.onerror = (error) => reject(error);
    });
  };

  const uploadSingleFileToGAS = async (file) => {
    let fileToUpload = file;
    if (file.type.startsWith("image/")) {
      try { fileToUpload = await imageCompression(file, { maxSizeMB: 2, maxWidthOrHeight: 1920, useWebWorker: true }); } 
      catch (err) { }
    }
    const base64Full = await fileToBase64(fileToUpload);
    const base64Data = base64Full.split(",")[1];
    const URL_GAS = process.env.REACT_APP_GOOGLE_SCRIPT_ACTION_URL;
    const payloadGAS = JSON.stringify({ action: "uploadBukti", fileBase64: base64Data, mimeType: file.type });
    const res = await fetch(URL_GAS, { method: "POST", body: payloadGAS, headers: { "Content-Type": "text/plain" } });
    const result = await res.json();
    if (result.result === "success") return result.message.buktiUrl;
    else throw new Error("Gagal upload ke Google Drive");
  };

  const handleFetchMyKpi = async () => {
    try {
      if (!user?.email) return;
      setActiveTab("kpi"); setLoadingKpi(true); setLoadingTabungan(false);
      const response = await fetch(`${API}/api/kpi-by-user`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: user.email }) });
      const data = await response.json();
      if (data.result !== "success") throw new Error(data.message || "Gagal mengambil KPI");

      const grouped = {}; const counts = {}; let totalKpiCount = 0;
      setIsSuperAdmin(Boolean(data.is_super_admin));
      const kpiList = Array.isArray(data.data) ? data.data : [];

      kpiList.forEach((kpi) => {
        const kpiId = kpi.id || kpi.kpi_id;
        if (kpiId) {
          const nama = kpi.nama || "Tanpa Nama";
          if (!grouped[nama]) grouped[nama] = [];
          grouped[nama].push({ ...kpi, id: kpiId, bukti: kpi.bukti_nilai || null, edit_count: Number(kpi.count) || 0, can_edit: Boolean(kpi.can_edit), indikator_kpi: kpi.indikator_kpi });
          counts[kpiId] = Number(kpi.count) || 0; totalKpiCount++;
        }
      });
      setMyKpiData(grouped); setEditCounts((prev) => ({ ...prev, ...counts })); setTotalKpi(totalKpiCount); setTotalKaryawan(Object.keys(grouped).length);
    } catch (err) { setModalData({ isOpen: true, type: "error", title: "Gagal Fetch KPI", message: err.message });
    } finally { setLoadingKpi(false); }
  };

  const handleOpenModal = () => {
    if (!user) { setModalData({ isOpen: true, type: "error", title: "User Tidak Terdeteksi", message: "Silakan login ulang!" }); return; }
    setShowForm(true);
  };
  const handleCloseModal = () => setShowForm(false);

  const toggleDate = (date) => { setExpandedDates((prev) => prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]); };

  const openEditModal = (id, groupName, actual, bukti, canEdit, dataType = "KPI") => {
    if (!canEdit) { setModalData({ isOpen: true, type: "error", title: "Akses Ditolak", message: "Anda tidak memiliki izin untuk mengedit item ini." }); return; }
    let buktiType = null;
    if (bukti) buktiType = bukti.toLowerCase().endsWith(".pdf") ? "pdf" : "image";
    const actualDisplay = actual ? String(actual).replace(".", ",") : "";
    setEditModalData({ id, groupName, actual: actualDisplay, buktiUrl: bukti, buktiType, buktiFile: null, buktiPreview: null, buktiFilename: bukti ? extractFilenameFromUrl(bukti) : null, can_edit: canEdit, dataType: dataType });
  };
  const closeEditModal = () => setEditModalData(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    let type = file.type.includes("pdf") ? "pdf" : "image";
    let preview = type === "image" ? URL.createObjectURL(file) : null;
    setEditModalData((prev) => ({ ...prev, buktiFile: file, buktiPreview: preview, buktiType: type, buktiFilename: file.name }));
  };

  const confirmSaveChanges = async () => {
    if (!editModalData || !user?.email) return;
    const { id, actual, buktiFile, groupName, dataType, buktiUrl } = editModalData;
    const currentCount = editCounts[id] || 0;

    if (!isSuperAdmin && currentCount >= 2) {
      setModalData({ isOpen: true, type: "error", title: "Batas Edit Tercapai", message: "Anda hanya dapat mengedit item ini maksimal 2 kali." }); return;
    }

    setModalData({
      isOpen: true, type: "info", title: "Simpan Perubahan", message: "Yakin ingin menyimpan perubahan?", confirmOnly: true,
      onConfirm: async () => {
        try {
          setSavingData(true); let finalBuktiUrl = buktiUrl;
          if (buktiFile) { try { finalBuktiUrl = await uploadSingleFileToGAS(buktiFile); } catch (err) { throw new Error("Gagal mengupload file bukti baru."); } }

          const numericActual = parseFloat(actual.toString().replace(",", "."));
          if (isNaN(numericActual)) throw new Error("Format angka tidak valid.");

          const payload = { actual: numericActual, email: user.email, bukti: finalBuktiUrl };
          if (dataType === "KPI") payload.kpiKey = id; else payload.id = id;

          const endpoint = dataType === "KPI" ? `${API}/api/kpi-update` : `${API}/api/tabungan-update`;
          const response = await fetch(endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
          const result = await response.json();
          if (result.result !== "success") throw new Error(result.message || "Update gagal");

          const updateLocalState = (prev) => {
            const list = prev[groupName] || [];
            return { ...prev, [groupName]: list.map((item) => item.id === id ? { ...item, actual: numericActual, bukti: finalBuktiUrl } : item) };
          };

          if (dataType === "KPI") setMyKpiData(updateLocalState); else setMyTabunganData(updateLocalState);
          setEditCounts((p) => ({ ...p, [id]: (p[id] || 0) + 1 })); setEditModalData(null);
          setModalData({ isOpen: true, type: "success", title: "Berhasil", message: "Berhasil disimpan.", confirmOnly: true, confirmLabel: "Selesai", onConfirm: () => setModalData((p) => ({ ...p, isOpen: false })) });
        } catch (err) { setModalData({ isOpen: true, type: "error", title: "Gagal", message: err.message, onConfirm: () => setModalData((p) => ({ ...p, isOpen: false })) }); } finally { setSavingData(false); }
      },
    });
  };

  if (loadingPage) return <ScreenLoading />;

  const handleOpenTabunganModal = () => {
    if (!user) { setModalData({ isOpen: true, type: "error", title: "User Tidak Terdeteksi", message: "Silakan login ulang!" }); return; }
    setShowTabunganForm(true);
  };
  const handleCloseTabunganModal = () => setShowTabunganForm(false);

  const handleFetchTabungan = async () => {
    try {
      const userEmail = user?.email || user?.user?.email;
      if (!userEmail) throw new Error("User belum login!");
      setActiveTab("tabungan"); setLoadingTabungan(true); setLoadingKpi(false);

      const response = await fetch(`${API}/api/tabungan-by-user`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: userEmail }) });
      const data = await response.json();
      if (data.result !== "success") throw new Error(data.message || "Gagal memuat data tabungan");

      const grouped = {}; const counts = {}; let totalCount = 0;
      setIsSuperAdmin(Boolean(data.is_super_admin));
      const tabunganList = Array.isArray(data.data) ? data.data : [];

      tabunganList.forEach((item) => {
        const itemId = item.id || item.tabungan_id;
        if (itemId) {
          const nama = item.nama || "Tanpa Nama";
          if (!grouped[nama]) grouped[nama] = [];
          grouped[nama].push({ ...item, id: itemId, title_display: item.kerja_tabungan_gaji || "Tabungan", param_display: item.parameter || "", bukti: item.bukti_nilai || null, actual: item.actual || 0, edit_count: Number(item.count) || 0, can_edit: Boolean(item.can_edit) });
          counts[itemId] = Number(item.count) || 0; totalCount++;
        }
      });
      setMyTabunganData(grouped); setEditCounts((prev) => ({ ...prev, ...counts })); setTotalTabungan(totalCount); setTotalKaryawan(Object.keys(grouped).length);
    } catch (err) { setModalData({ isOpen: true, type: "error", title: "Gagal", message: err.message }); } finally { setLoadingTabungan(false); }
  };

  const formatDisplayNumber = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    if (typeof val === "string" && val.includes("T") && val.includes("Z")) return "Error Format"; 
    return String(val).replace(".", ",");
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  const taskCount = pendingApprovals.filter(p => p.isTask).length;

  return (
    <div className="homepage-container">
      <header className="homepage-header-modern">
        <div className="header-left">
          <div className="logo-wrapper"><img src={logoAnnisa} alt="RSIA ANNISA" className="header-logo" /></div>
          <div className="brand-info">
            <h1>RSIA ANNISA</h1>
            <div className="date-badge"><Calendar size={12} /><span>{getCurrentDate()}</span></div>
          </div>
        </div>

        <div className="header-right">
          <div className="notif-bell-container" onClick={() => setShowPendingModal(true)}>
            <Bell size={24} color="#4b5563" />
            {taskCount > 0 && (
              <span className="notif-badge-count">{taskCount}</span>
            )}
          </div>
          <div className="user-profile-box">
            <span className="user-name">{user?.name}</span>
            <div className="user-avatar-frame">
              {user?.photo ? (<img src={user.photo} alt="Profile" referrerPolicy="no-referrer" />) : (<div className="avatar-placeholder"><UserIcon size={20} /></div>)}
            </div>
          </div>
          <button className="logout-btn-modern" onClick={() => setModalData({ isOpen: true, type: "warning", title: "Konfirmasi Keluar", message: "Apakah Anda yakin ingin mengakhiri sesi ini?", confirmLabel: "Keluar", onConfirm: async () => { setShowLogoutModal(false); handleLogout(); }, confirmOnly: false })} title="Logout"><LogOut size={20} /></button>
        </div>
      </header>

      <main className="homepage-main">
        <div className="add-master-container">
          <button onClick={() => setShowAddMasterModal(true)} className="btn-add-master">
            <FilePlus size={20} /> Tambah Indikator Baru
          </button>
        </div>

        <div className="management-cards">
          <div className={`manage-card kpi-theme ${activeTab === "kpi" ? "active-card" : ""}`}>
            <div className="card-bg-icon"><BarChart3 size={120} strokeWidth={0.5} /></div>
            <div className="manage-card-content">
              <div className="manage-card-header"><div className="icon-badge kpi-badge"><BarChart3 size={24} /></div><div><h3>KPI</h3><p>Pantau performa & evaluasi karyawan</p></div></div>
              <div className="manage-card-actions">
                <button className="manage-btn primary-btn" onClick={handleOpenModal}><PlusCircle size={18} />Input KPI</button>
                <button className={`manage-btn secondary-btn ${activeTab === "kpi" ? "btn-active" : ""}`} onClick={handleFetchMyKpi}><Layout size={18} />Kelola KPI</button>
              </div>
            </div>
          </div>

          <div className={`manage-card tabungan-theme ${activeTab === "tabungan" ? "active-card" : ""}`}>
            <div className="card-bg-icon"><Wallet size={120} strokeWidth={0.5} /></div>
            <div className="manage-card-content">
              <div className="manage-card-header"><div className="icon-badge tabungan-badge"><Wallet size={24} /></div><div><h3>Tabungan</h3><p>Simpanan & riwayat tabungan</p></div></div>
              <div className="manage-card-actions">
                <button className="manage-btn primary-btn" onClick={handleOpenTabunganModal}><PlusCircle size={18} />Input Tabungan</button>
                <button className={`manage-btn secondary-btn ${activeTab === "tabungan" ? "btn-active" : ""}`} onClick={handleFetchTabungan}><Layout size={18} />Kelola Tabungan</button>
              </div>
            </div>
          </div>
        </div>

        {(loadingKpi || loadingTabungan) && (
          <div className="kpi-loading-wrapper">
            <div className="kpi-loading-box"><div className="modern-spinner"></div><p>Memuat Data <span>{activeTab === "kpi" ? "KPI" : "Tabungan"}</span>...</p></div>
          </div>
        )}

        {!loadingKpi && !loadingTabungan && activeTab && (
          <>
            <div className="kpi-top-section">
              <div className="kpi-summary-box">
                <div className="kpi-summary-item"><div className="kpi-summary-number">{totalKaryawan}</div><div className="kpi-summary-label">Karyawan Terdata</div></div>
                <div className="kpi-summary-item"><div className="kpi-summary-number">{activeTab === "kpi" ? totalKpi : totalTabungan}</div><div className="kpi-summary-label">Total Item {activeTab === "kpi" ? "KPI" : "Tabungan"}</div></div>
              </div>

              <div className="kpi-search-wrapper">
                <Search className="kpi-search-icon" size={18} />
                <input type="text" className="kpi-search-input" placeholder="Cari nama karyawan..." value={searchName} onChange={(e) => { setSearchName(e.target.value); setShowSuggest(true); }} onFocus={() => setShowSuggest(true)} onBlur={() => setTimeout(() => setShowSuggest(false), 150)} />
                {showSuggest && filteredSuggestions.length > 0 && (
                  <ul className="kpi-suggest-list">
                    {filteredSuggestions.map((name) => (<li key={name} className="kpi-suggest-item" onClick={() => { setSearchName(name); setShowSuggest(false); }}>{name}</li>))}
                  </ul>
                )}
              </div>
            </div>

            <div className="section-header-modern">
              <div className={`section-icon-box ${activeTab === "kpi" ? "icon-toska" : "icon-green"}`}>{activeTab === "kpi" ? <FileText size={24} /> : <Wallet size={24} />}</div>
              <div className="section-text"><h2>{activeTab === "kpi" ? "Data KPI Karyawan" : "Data Tabungan Karyawan"}</h2></div>
              <div className="section-line-decor"></div>
            </div>

            <div className="my-kpi-cards-wrapper">
              {Object.entries(currentData).filter(([nama]) => searchName ? nama.toLowerCase().includes(searchName.toLowerCase()) : true).sort(([a], [b]) => a.localeCompare(b)).map(([nama, items]) => {
                  const isOpen = expandedDates.includes(nama);
                  return (
                    <div key={nama} className={`kpi-card-modern ${isOpen ? "is-open" : ""}`}>
                      <div className="kpi-card-header-modern" onClick={() => toggleDate(nama)}>
                        <div className="header-left">
                          <div className={`header-icon-box ${activeTab === "kpi" ? "bg-toska" : "bg-green"}`}>{activeTab === "kpi" ? <FileText size={20} /> : <Wallet size={20} />}</div>
                          <div className="header-info"><h3 className="header-title">{nama}</h3><span className="header-subtitle">{items.length} Data Tersimpan</span></div>
                        </div>

                        <div className="header-right-action">
                          {/* 🔥 TOMBOL CETAK SELALU MUNCUL: Gembok sudah dilepas! 🔥 */}
                          <button 
                            className="btn-print-kpi" 
                            title="Cetak Laporan ke Tab Baru"
                            onClick={(e) => { 
                              e.preventDefault();
                              e.stopPropagation();
                              localStorage.setItem("printData_RSIA", JSON.stringify({ nama, items, type: activeTab, allNames, currentUser: user?.name }));
                              window.open(`${window.location.origin}/indikator-kpi/print`, "_blank");
                            }}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px', color: 'inherit', padding: 0, font: 'inherit' }}
                          >
                            <Printer size={16} /> Cetak
                          </button>
                          
                          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>

                      <div className={`kpi-content-wrapper ${isOpen ? "show" : ""}`}>
                        <div className="kpi-content-inner">
                          {items.map((item) => (
                            <div key={item.id} className="kpi-item-row">
                              <div className="row-main-info"><span className="row-indicator">{activeTab === "kpi" ? item.indikator_kpi : item.title_display}</span></div>
                              <div className="row-meta-info">
                                <div className="row-value-badge"><span className="label">Actual</span><span className="value">{formatDisplayNumber(item.actual)}</span></div>
                                <div className="row-actions">
                                  <div className={`action-btn btn-view ${(!item.bukti || item.bukti === "") ? "disabled-view" : ""}`} title={item.bukti ? "Lihat Bukti" : "Bukti belum tersedia"} onClick={(e) => { e.stopPropagation(); if (item.bukti && item.bukti !== "") { window.open(item.bukti, "_blank"); } else { setModalData({ isOpen: true, type: "info", title: "Bukti Kosong", message: "Belum ada lampiran bukti untuk data ini.", confirmOnly: true, onConfirm: () => setModalData((prev) => ({ ...prev, isOpen: false })) }); } }}><Eye size={18} /></div>
                                  {(isSuperAdmin || item.can_edit === true) && (
                                    <button className="action-btn btn-edit" title="Edit Data" disabled={!isSuperAdmin && Number(item.edit_count) >= 2} onClick={(e) => { e.stopPropagation(); openEditModal(item.id, nama, item.actual, item.bukti, isSuperAdmin || item.can_edit, activeTab === "kpi" ? "KPI" : "TABUNGAN"); }}><Edit3 size={18} /></button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </>
        )}
      </main>

      <footer className="homepage-footer"><p>© {new Date().getFullYear()} Rumah Sakit Annisa — Mendampingi Sedekat Sahabat</p></footer>

      {showForm && user && <KpiFormModal onClose={handleCloseModal} user={user} />}
      {showTabunganForm && user && <TabunganFormModal user={user} onClose={handleCloseTabunganModal} />}

      {showAddMasterModal && (
        <AddMasterModal 
          initialTab={activeTab || "kpi"} 
          onClose={() => setShowAddMasterModal(false)} 
          onSuccess={() => {
            setShowAddMasterModal(false);
          }} 
        />
      )}

      {showPendingModal && (
        <div className="ekp-overlay" onClick={() => setShowPendingModal(false)}>
          <div className="ekp-modal notif-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="notif-modal-header">
              <span>Notifikasi & Riwayat Persetujuan</span>
              <span className="notif-close-btn" onClick={() => setShowPendingModal(false)}>✖</span>
            </h3>
            
            {loadingNotifs ? (
              <div className="notif-loading-container">
                <div className="modern-spinner"></div>
                <p className="notif-loading-text">Memuat riwayat persetujuan...</p>
              </div>
            ) : pendingApprovals.length === 0 ? (
              <p className="notif-empty-text">Tidak ada notifikasi atau riwayat laporan.</p>
            ) : (
              <div className="notif-list-container">
                {pendingApprovals.map((req, idx) => (
                  <div key={idx} className={`notif-item ${req.isTask ? 'task' : 'history'}`}>
                    <div>
                      <h4 className="notif-item-title">Laporan {req.type.toUpperCase()} - {req.nama_karyawan}</h4>
                      
                      {req.isOwner ? (
                        req.status === "APPROVED" ? (
                          <p className="notif-status-text text-success">✅ Laporan Anda Selesai (Disetujui)</p>
                        ) : req.ttd1 ? (
                          <p className="notif-status-text text-primary">⏳ Menunggu TTD Atasan 2</p>
                        ) : (
                          <p className="notif-status-text text-warning">⏳ Menunggu TTD Atasan 1</p>
                        )
                      ) : req.isTask ? (
                        <p className="notif-status-text text-danger">🔴 Belum Di Tanda Tangan</p>
                      ) : (
                        req.status === "APPROVED" ? (
                          <p className="notif-status-text text-success">✅ Selesai Ditanda tangani</p>
                        ) : req.ttd1 ? (
                          <p className="notif-status-text text-muted">⏳ Sudah TTD Atasan 1 (Menunggu Atasan 2)</p>
                        ) : (
                          <p className="notif-status-text text-muted">⏳ Menunggu TTD Atasan 1</p>
                        )
                      )}
                    </div>

                    <div>
                      {req.isTask ? (
                         <button 
                           type="button"
                           className="btn-notif-action"
                           onClick={async (e) => {
                             e.preventDefault(); 
                             e.stopPropagation();
                             const btn = e.currentTarget;
                             const originalText = btn.innerText;
                             btn.innerText = "Memuat...";
                             btn.disabled = true;

                             try {
                               const res = await fetch(`${API}/api/${req.type}-by-user`, { 
                                 method: "POST", 
                                 headers: { "Content-Type": "application/json" }, 
                                 body: JSON.stringify({ email: user.email }) 
                               });
                               
                               const data = await res.json();
                               
                               if (data.result !== "success") {
                                 throw new Error(data.message || "Gagal mengambil data laporan dari server.");
                               }
                               
                               const userItems = (data.data || []).filter(item => cleanStr(item.nama) === cleanStr(req.nama_karyawan));
                               
                               if (userItems.length === 0) {
                                 alert("Data laporan tidak ditemukan atau Anda tidak memiliki akses.");
                                 btn.innerText = originalText;
                                 btn.disabled = false;
                                 return;
                               }

                               localStorage.setItem("printData_RSIA", JSON.stringify({ 
                                 nama: req.nama_karyawan, 
                                 items: userItems, 
                                 type: req.type, 
                                 currentUser: user?.name 
                               }));

                               setShowPendingModal(false);
                               
                               window.open(`${window.location.origin}/indikator-kpi/print`, "_blank");
                               
                             } catch (err) {
                               console.error("Gagal membuka halaman print:", err);
                               alert("Terjadi kesalahan: " + err.message);
                             } finally {
                               btn.innerText = originalText;
                               btn.disabled = false;
                             }
                           }}
                         >
                           Buka & TTD
                         </button>
                      ) : (
                        <span className="badge-history">Riwayat</span>
                      )}
                    </div>

                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Modal isOpen={modalData.isOpen} type={modalData.type} title={modalData.title} message={modalData.message} confirmOnly={modalData.confirmOnly} confirmLabel={modalData.confirmLabel} onClose={() => setModalData((prev) => ({ ...prev, isOpen: false, onConfirm: null }))} onConfirm={() => { const action = modalData.onConfirm; setModalData((prev) => ({ ...prev, isOpen: false, onConfirm: null })); if (action) setTimeout(() => action(), 0); }} />
      <Modal isOpen={showLogoutModal} type="warning" title="Konfirmasi Logout" message="Apakah kamu yakin ingin keluar?" onClose={() => setShowLogoutModal(false)} onConfirm={() => { setShowLogoutModal(false); handleLogout(); }} />

      {editModalData && (
        <div className="ekp-overlay">
          <div className="ekp-modal">
            <h3 className="ekp-title">Edit {editModalData.dataType === "KPI" ? "KPI" : "Tabungan"}</h3>
            {!isSuperAdmin && (<p className="ekp-sisa-edit">Sisa edit: <strong>{Math.max(0, 2 - (editCounts[editModalData.id] || 0))} kali lagi</strong></p>)}
            <div className="ekp-field">
              <label>Actual:</label>
              <input type="text" inputMode="decimal" value={editModalData.actual} onChange={(e) => { let val = e.target.value; val = val.replace(".", ","); if (val === "" || /^[0-9,]*$/.test(val)) { if ((val.match(/,/g) || []).length <= 1) setEditModalData((prev) => ({ ...prev, actual: val })); } }} />
            </div>
            <div className="ekp-field">
              <label>Bukti (opsional):</label>
              <div className="ekp-file-box">
                <input type="file" id="file-upload" className="ekp-hidden-input" onChange={handleFileChange} />
                <label htmlFor="file-upload" className="ekp-btn">Pilih File</label>
                <span className="ekp-filename">{editModalData.buktiFilename ? shortenFilename(editModalData.buktiFilename) : "No file chosen"}</span>
              </div>
              {editModalData.buktiFile && (
                <div className="ekp-preview-container">
                  {editModalData.buktiType === "image" && (<img src={editModalData.buktiPreview} alt="Preview" className="ekp-img-preview" />)}
                  {editModalData.buktiType === "pdf" && (<p className="ekp-pdf-preview">PDF diPilih — <a href={URL.createObjectURL(editModalData.buktiFile)} target="_blank" rel="noopener noreferrer">Lihat PDF</a></p>)}
                </div>
              )}
            </div>
            <div className="ekp-footer">
              <button className="ekp-save" onClick={confirmSaveChanges} disabled={savingData || (!editModalData?.can_edit && !isSuperAdmin)}>{savingData ? "Menyimpan..." : "Simpan"}</button>
              <button className="ekp-cancel" onClick={closeEditModal} disabled={savingData}>Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;